import {
  NotificationProps,
  NotificationOptions,
  NotificationColor,
  NotificationGroup,
  NotificationConfig,
  NotifyMessageOptions,
  NotifyMessageCardFields,
  NotifyMessageStackGroup,
  resolveNotifyMessageStackKey,
} from '@/shared/types/message';
import { buildNotificationPropsForEnqueue } from './notificationServiceBuildEnqueueNotification';
import {
  mergeNotificationCardFromNotifyMessageOptions,
  stackKeyToQueueColor,
} from './notificationServiceMergeCard';
import { normalizeNotifyMessageOptions } from './notificationServiceNormalizeOptions';

// Listener para notificaciones agrupadas
type NotificationGroupListener = (groups: Record<string, NotificationGroup>) => void;

// Configuración por defecto
const defaultNotificationConfig: NotificationConfig = {
  position: 'top-right',
  duration: 5000,
  animationDuration: 300,
  size: 'sm',
  showTimer: true,
  stackMax: 3,
};

const notificationConfig: NotificationConfig = { ...defaultNotificationConfig };

/**
 * Servicio de notificaciones agrupadas por `stackKey` (`primary` / `secondary` / `auto`), cola antigua → reciente
 * y `stackMax`. El temporizador y la barra de progreso aplican solo a la **más reciente**; al expirar se vacía
 * el mazo entero (`dismissStack`). `dismiss(id)` sobre la más reciente equivale a vaciar el mazo; sobre otra
 * entrada solo retira esa. `dismissGroup` / `dismissStack` vacían mazos con animación.
 */
class NotificationService {
  private listeners: NotificationGroupListener[] = [];
  private idCounter = 0;

  private groups = new Map<string, NotificationGroup>();

  private timers = new Map<string, NodeJS.Timeout | null>();

  private progressIntervals = new Map<string, NodeJS.Timeout | null>();

  private removeTimers = new Map<string, NodeJS.Timeout | null>();

  /**
   * Encola una notificación agrupada (API unificada `notify.message`).
   */
  message(options: NotifyMessageOptions): number {
    const normalized = normalizeNotifyMessageOptions(options);
    const stackKey = resolveNotifyMessageStackKey(normalized);
    const notificationCard = mergeNotificationCardFromNotifyMessageOptions(normalized);
    const {
      stackGroup: _sg,
      notification: _nested,
      content: _c,
      leading: _l,
      appearance: _a,
      meta: _m,
      events: _e,
      footer: _f,
      cta: _cta,
      stackSummaryLegend,
      ...rest
    } = normalized;
    const color = stackKeyToQueueColor(stackKey);
    return this.enqueueFromOptions(rest, stackKey, color, notificationCard, stackSummaryLegend);
  }

  private enqueueFromOptions(
    finalOptions: NotificationOptions & { title?: string },
    stackKey: string,
    color: NotificationColor,
    notificationCard?: NotifyMessageCardFields,
    stackSummaryLegend?: string
  ): number {
    const id = Date.now() + ++this.idCounter;
    const notification = buildNotificationPropsForEnqueue(
      id,
      finalOptions,
      color,
      notificationCard,
      notificationConfig
    );

    this.addToGroup(stackKey, color, notification, stackSummaryLegend);

    return id;
  }

  /**
   * Si la cola supera `stackMax`, elimina la más antigua hasta cumplir el límite.
   */
  private evictOldestWhileOverCap(stackKey: string) {
    const group = this.groups.get(stackKey);
    if (!group) return;

    const max = notificationConfig.stackMax;
    while (group.queue.length > max) {
      const removed = group.queue.shift();
      removed?.onClose?.();
    }

    if (group.queue.length === 0) {
      this.clearTimer(stackKey);
      this.groups.delete(stackKey);
    } else {
      group.notification = group.queue[group.queue.length - 1];
      group.count = group.queue.length;
    }
  }

  /**
   * Actualiza la leyenda del pie de apilado según la última llamada a `notify.message` para ese mazo.
   */
  private applyStackSummaryLegend(group: NotificationGroup, legend: string | undefined) {
    if (legend !== undefined && legend.trim() !== '') {
      group.stackSummaryLegend = legend;
    } else {
      delete group.stackSummaryLegend;
    }
  }

  /**
   * Cancela solo el timeout que borra el mazo tras `dismissStack` (no toca timers de progreso).
   */
  private cancelStackRemovalTimer(stackKey: string) {
    const r = this.removeTimers.get(stackKey);
    if (r) {
      clearTimeout(r);
      this.removeTimers.set(stackKey, null);
    }
  }

  /**
   * Agrega una notificación al grupo correspondiente
   */
  private addToGroup(
    stackKey: string,
    color: NotificationColor,
    notification: NotificationProps,
    stackSummaryLegend?: string
  ) {
    let existingGroup = this.groups.get(stackKey);

    // Mazo en salida: aún está en el mapa hasta el fin de la animación; no reutilizar la cola moribunda.
    if (existingGroup?.isLeaving) {
      this.cancelStackRemovalTimer(stackKey);
      this.groups.delete(stackKey);
      existingGroup = undefined;
    }

    if (existingGroup) {
      existingGroup.queue.push(notification);
      this.evictOldestWhileOverCap(stackKey);
      const group = this.groups.get(stackKey);
      if (!group) {
        this.notifyListeners();
        return;
      }
      group.notification = group.queue[group.queue.length - 1];
      group.count = group.queue.length;
      this.applyStackSummaryLegend(group, stackSummaryLegend);
      this.resetTimer(stackKey);
    } else {
      const newGroup: NotificationGroup = {
        stackKey,
        color,
        notification,
        queue: [notification],
        count: 1,
        timerProgress: 100,
        isLeaving: false,
      };
      this.applyStackSummaryLegend(newGroup, stackSummaryLegend);
      this.groups.set(stackKey, newGroup);
      this.evictOldestWhileOverCap(stackKey);
      if (!this.groups.get(stackKey)) {
        this.notifyListeners();
        return;
      }
      this.startTimer(stackKey);
    }

    this.notifyListeners();
  }

  /**
   * Inicia el timer del grupo solo para la notificación **más reciente** (`queue[queue.length - 1]`).
   * Al expirar se vacía el mazo con `dismissStack` (las demás no tienen timer propio).
   */
  private startTimer(stackKey: string) {
    const group = this.groups.get(stackKey);
    if (!group || group.queue.length === 0) return;

    const newest = group.queue[group.queue.length - 1];
    if (newest.id === undefined) return;

    const duration = newest.duration ?? notificationConfig.duration;
    if (duration <= 0) return;

    this.clearTimer(stackKey);

    const startTime = Date.now();
    group.timerProgress = 100;

    this.progressIntervals.set(
      stackKey,
      setInterval(() => {
        const elapsed = Date.now() - startTime;
        const progress = Math.max(0, 100 - (elapsed / duration) * 100);

        const g = this.groups.get(stackKey);
        if (g) {
          g.timerProgress = progress;
          this.notifyListeners();
        }
      }, 50)
    );

    this.timers.set(
      stackKey,
      setTimeout(() => {
        this.dismissStack(stackKey);
      }, duration)
    );
  }

  /**
   * Reinicia el timer para un grupo
   */
  private resetTimer(stackKey: string) {
    this.clearTimer(stackKey);
    this.startTimer(stackKey);
  }

  /**
   * Limpia el timer de un grupo
   */
  private clearTimer(stackKey: string) {
    const t = this.timers.get(stackKey);
    if (t) {
      clearTimeout(t);
      this.timers.set(stackKey, null);
    }
    const p = this.progressIntervals.get(stackKey);
    if (p) {
      clearInterval(p);
      this.progressIntervals.set(stackKey, null);
    }
    const r = this.removeTimers.get(stackKey);
    if (r) {
      clearTimeout(r);
      this.removeTimers.set(stackKey, null);
    }
  }

  /**
   * Cierra todas las notificaciones de un mazo explícito (`primary` | `secondary`).
   */
  dismissGroup(stackGroup: NotifyMessageStackGroup) {
    this.dismissStack(stackGroup);
  }

  /**
   * Cierra todas las notificaciones del mazo identificado por `stackKey` (`primary`, `secondary`, `auto`, …).
   */
  dismissStack(stackKey: string) {
    const group = this.groups.get(stackKey);
    if (!group) return;

    for (const n of group.queue) {
      n.onClose?.();
    }

    group.isLeaving = true;
    this.notifyListeners();

    this.clearTimer(stackKey);

    this.removeTimers.set(
      stackKey,
      setTimeout(() => {
        this.groups.delete(stackKey);
        this.removeTimers.set(stackKey, null);
        this.notifyListeners();
      }, notificationConfig.animationDuration)
    );
  }

  /**
   * Elimina una notificación concreta de su grupo por `id`.
   * Si `id` es la más reciente del mazo, equivale a `dismissStack` (cierra todo el grupo).
   * Si no, retira solo esa entrada y reinicia el timer para la nueva más reciente.
   */
  dismiss(id: number) {
    for (const [stackKey, group] of this.groups) {
      const newest = group.queue[group.queue.length - 1];
      if (newest?.id === id) {
        this.dismissStack(stackKey);
        return;
      }

      const idx = group.queue.findIndex((n) => n.id === id);
      if (idx === -1) continue;

      const [removed] = group.queue.splice(idx, 1);
      removed?.onClose?.();

      if (group.queue.length === 0) {
        this.clearTimer(stackKey);
        this.groups.delete(stackKey);
        this.notifyListeners();
        return;
      }

      group.notification = group.queue[group.queue.length - 1];
      group.count = group.queue.length;

      this.resetTimer(stackKey);

      this.notifyListeners();
      return;
    }
  }

  /**
   * Configura las opciones del servicio
   */
  configure(options: Partial<NotificationConfig>) {
    Object.assign(notificationConfig, options);

    if (
      notificationConfig.stackMax !== undefined &&
      (!Number.isFinite(notificationConfig.stackMax) || notificationConfig.stackMax < 1)
    ) {
      if (process.env.NODE_ENV !== 'production') {
        // eslint-disable-next-line no-console
        console.warn(
          `[NotificationService] Invalid stackMax "${notificationConfig.stackMax}". Using default ${defaultNotificationConfig.stackMax}.`
        );
      }
      notificationConfig.stackMax = defaultNotificationConfig.stackMax;
    }
  }

  /**
   * Obtiene los grupos actuales (solo mazos con cola activa).
   */
  getGroups(): Record<string, NotificationGroup> {
    return Object.fromEntries(this.groups);
  }

  /**
   * Suscribirse a cambios
   */
  subscribe(listener: NotificationGroupListener): () => void {
    this.listeners.push(listener);
    return () => {
      const index = this.listeners.indexOf(listener);
      if (index > -1) {
        this.listeners.splice(index, 1);
      }
    };
  }

  /**
   * Notifica a todos los listeners
   */
  private notifyListeners() {
    const groups = this.getGroups();
    this.listeners.forEach((listener) => listener(groups));
  }

  /**
   * Obtiene la configuración actual
   */
  getConfig(): NotificationConfig {
    return { ...notificationConfig };
  }
}

/**
 * Instancia singleton del servicio de notificaciones agrupadas; la usan `notification`, `notify` y `useNotification`.
 */
export const notificationService = new NotificationService();

/**
 * Expone el mismo singleton que `notificationService` (acceso directo al servicio desde componentes o pruebas).
 *
 * @returns Instancia de `NotificationService` compartida en toda la app.
 */
export const useNotificationService = () => {
  return notificationService;
};
